// Store routing for the download badges. Okiri's Android distribution is
// PalmStore (Transsion) + Samsung Galaxy Store + APKPure (universal fallback);
// iOS is the App Store. There is NO Google Play listing, so any Android device
// we can't place by brand is sent to APKPure.
//
// The bug this fixes: the PalmStore *deep link* (palmplay://) only resolves on
// Transsion devices (Tecno/Infinix/itel). Returning it for every mobile UA made
// non-Transsion phones show "cannot open because the address is invalid".

export const PALM_STORE_WEB =
  "https://www.palmplaystore.com/detail/com.okirimobile.app?country=NG";
export const PALM_STORE_MOBILE =
  "palmplay://thirdlauncher.com/?entryType=AppDetail&packageName=com.okirimobile.app";
export const APKPURE_WEB = "https://apkpure.com/p/com.okirimobile.app";

const TRANSSION_RE = /tecno|infinix|itel|transsion/i;
const SAMSUNG_RE = /samsung|\bSM-|\bGT-/i;

function ua() {
  if (typeof navigator === "undefined") return "";
  return navigator.userAgent || navigator.vendor || "";
}

/** 'ios' | 'android' | 'desktop' — best-effort from UA + client hints. */
export function getPlatform() {
  if (typeof window === "undefined") return "desktop";
  const u = ua();
  // iPadOS 13+ reports as Mac; disambiguate via touch points.
  if (/iphone|ipad|ipod/i.test(u) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
    return "ios";
  }
  if (/android/i.test(u) || navigator.userAgentData?.platform === "Android") return "android";
  return "desktop";
}

/** Best-effort Android brand from the UA string: 'transsion' | 'samsung' | 'unknown'. */
export function getBrand() {
  const u = ua();
  if (TRANSSION_RE.test(u)) return "transsion";
  if (SAMSUNG_RE.test(u)) return "samsung";
  return "unknown";
}

function isTranssionMobile() {
  return getPlatform() === "android" && getBrand() === "transsion";
}

/**
 * The PalmStore href for the current device. Only Transsion devices can resolve
 * the palmplay:// scheme, so everyone else (other Android + desktop) gets the web
 * listing — which always loads instead of dead-ending with "address is invalid".
 */
export function getPalmStoreHref() {
  if (typeof window === "undefined") return PALM_STORE_WEB;
  return isTranssionMobile() ? PALM_STORE_MOBILE : PALM_STORE_WEB;
}

/**
 * The single store to PROMOTE for this device (badge highlight/ordering):
 *   iOS → apple · Transsion → palm · Samsung → galaxy · any other Android → apkpure.
 * Desktop returns null (show the full row, no single recommendation).
 * @returns {'apple'|'palm'|'galaxy'|'apkpure'|null}
 */
export function getRecommendedStoreKey() {
  const p = getPlatform();
  if (p === "ios") return "apple";
  if (p === "android") {
    const b = getBrand();
    if (b === "transsion") return "palm";
    if (b === "samsung") return "galaxy";
    return "apkpure";
  }
  return null;
}

/** Map a badge's display name to a store key so a component can flag the recommended badge. */
export function badgeKey(name = "") {
  const n = name.toLowerCase();
  if (n.includes("palm")) return "palm";
  if (n.includes("galaxy") || n.includes("samsung")) return "galaxy";
  if (n.includes("app store") || n.includes("apple")) return "apple";
  if (n.includes("apk")) return "apkpure";
  return "other";
}

/**
 * Given a component's badge array, resolve the PalmStore href, flag the badge
 * recommended for this device, and move it first. One call so all three download
 * rows (Hero/Header/CTA) behave identically.
 */
export function resolveStoreBadges(badges) {
  const recKey = getRecommendedStoreKey();
  const resolved = badges.map((item) => ({
    ...item,
    href: badgeKey(item.name) === "palm" ? getPalmStoreHref() : item.href,
    recommended: !!recKey && badgeKey(item.name) === recKey,
  }));
  return resolved.sort((a, b) => (b.recommended ? 1 : 0) - (a.recommended ? 1 : 0));
}
